"use client";

import { useState } from "react";
import { ArrowUpRight, Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const manifestoItems = [{
  question: "How do you optimize OTA channel performance?",
  answer: "We conduct a deep-dive audit of your current OTA listings, optimizing content, imagery, and rate parity to improve visibility and conversion rates. Our data-driven approach ensures your property ranks higher and captures more direct-intent bookings.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_optimization_strategy.webp",
  imageAlt: "Data dashboard showing OTA performance metrics"
}, {
  question: "What hospitality tech stacks do you recommend?",
  answer: "We evaluate your current infrastructure and recommend scalable PMS, Channel Manager, and Booking Engine solutions that integrate seamlessly. Our goal is to streamline operations and reduce manual workload for your front-desk team.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_stack_integration.webp",
  imageAlt: "Modern hotel technology interface on tablet"
}, {
  question: "What is included in a revenue audit?",
  answer: "Our comprehensive audit covers historical performance analysis, competitor benchmarking, rate strategy evaluation, and distribution channel efficiency. You receive an actionable roadmap to unlock hidden revenue potential.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit_report.png",
  imageAlt: "Professional revenue audit document on desk"
}, {
  question: "How do you handle rate parity issues?",
  answer: "We monitor your rates across all channels in real-time. When discrepancies are detected, we provide the technical guidance or direct intervention needed to align your pricing, protecting your brand integrity and direct booking margins.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/rate_parity_monitoring.png",
  imageAlt: "Monitoring screen showing rate parity data"
}, {
  question: "Can you manage revenue remotely?",
  answer: "Yes, our remote revenue management services provide you with a dedicated expert who acts as an extension of your team. We handle daily pricing, forecasting, and strategy adjustments to maximize your RevPAR.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/remote_revenue_management.png",
  imageAlt: "Consultant working on revenue strategy remotely"
}, {
  question: "How do you improve direct booking conversion?",
  answer: "We optimize your website's booking path, implement persuasive call-to-actions, and leverage digital marketing strategies to drive traffic to your own booking engine, reducing reliance on high-commission third-party platforms.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/direct_booking_growth.png",
  imageAlt: "Hotel website booking interface on laptop"
}, {
  question: "What is your approach to dynamic pricing?",
  answer: "We utilize market demand signals, competitor pricing, and your property's historical data to implement dynamic pricing models. This ensures your rates are always optimized for maximum occupancy and average daily rate.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/dynamic_pricing_forecasting.webp",
  imageAlt: "Forecasting graph showing revenue growth"
}, {
  question: "How do we get started with HotelRevBaBa?",
  answer: "Simply book a consultation through our contact page. We will schedule a discovery call to understand your property's unique challenges and discuss how our revenue management strategies can drive your growth.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/consultation_booking.png",
  imageAlt: "Professional handshake in hotel lobby"
}];
export default function () {
  const [openValue, setOpenValue] = useState<string>("item-0");
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1664" id="frameworkfaqs" className="relative bg-background text-foreground py-24 md:py-32 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 md:px-16 lg:px-22">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5 relative">
            <div ref={headerRef} className={`lg:sticky lg:top-28 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              {}
              <div aria-hidden="true" className="pointer-events-none absolute -top-10 -left-4 lg:-left-8 select-none font-serif font-light leading-none text-foreground/5" style={{
              fontSize: "20vw"
            }}>
                05
              </div>

              {}
              <div className="relative flex items-center gap-3 mb-8">
                <span className="font-mono text-xs uppercase tracking-[0.18em] text-secondary">
                  § 05 / EXPERTISE — REVENUE STRATEGY
                </span>
              </div>

              {}
              <div className="relative flex items-center mb-10">
                <div className="h-px w-20 bg-secondary" />
                <div className="h-1 w-1 bg-secondary" />
              </div>

              {}
              <h2 className="relative font-default font-bold leading-[1.0] tracking-tight text-foreground mb-8" style={{
              fontSize: "clamp(2.25rem, 5.4vw, 4.75rem)"
            }}>
                Revenue Management{" "}
                <span className="italic text-secondary">Insights</span>
              </h2>

              {}
              <p className="relative font-default text-foreground/85 leading-relaxed mb-10 max-w-md" style={{
              fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)"
            }}>
                Expert answers to your most pressing hospitality revenue questions.
              </p>

              {}
              <Link to="/ContactPage" className="relative inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-secondary group">
                <span className="relative">
                  Book a Consultation
                  <span className="absolute left-0 -bottom-1 h-px bg-secondary transition-all duration-300 w-[60%] group-hover:w-full" />
                </span>
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible value={openValue} onValueChange={v => setOpenValue(v)} className="w-full">
              {manifestoItems.map((item, i) => {
              const itemValue = `item-${i}`;
              const isOpen = openValue === itemValue;
              const indexLabel = `${String(i + 1).padStart(2, "0")} / ${String(manifestoItems.length).padStart(2, "0")}`;
              return <AccordionItem key={i} data-index={i} value={itemValue} className="relative border-b-0 border-t border-foreground/20">
                    {}
                    <span aria-hidden="true" className={`pointer-events-none absolute left-0 top-0 w-[2px] bg-secondary transition-all duration-300 ease-out ${isOpen ? "h-full opacity-100" : "h-0 opacity-0"}`} />

                    <AccordionTrigger className={`group flex items-center justify-between gap-6 py-6 hover:no-underline rounded-md transition-colors duration-300 [&>svg]:hidden ${isOpen ? "text-foreground" : "text-foreground/85"}`}>
                      <span className="flex-1 text-left font-default font-semibold leading-tight tracking-tight" style={{
                    fontSize: "clamp(1.125rem, 1.8vw, 1.5rem)"
                  }}>
                        {item.question}
                      </span>
                      <span className="hidden sm:block font-mono text-xs uppercase tracking-[0.18em] text-foreground/55 shrink-0">
                        {indexLabel}
                      </span>
                      <span className={`shrink-0 inline-flex items-center justify-center w-7 h-7 text-secondary transition-all duration-300 ease-out ${isOpen ? "rotate-45 opacity-100" : "rotate-0 opacity-70 group-hover:opacity-100"}`} aria-hidden="true">
                        <Plus className="h-5 w-5" strokeWidth={1.25} />
                      </span>
                    </AccordionTrigger>

                    <AccordionContent className="pb-8 pt-2 pl-0 md:pl-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                        <p className="md:col-span-2 font-default text-foreground/85 leading-relaxed" style={{
                      fontSize: "0.9375rem",
                      lineHeight: 1.7
                    }}>
                          {item.answer}
                        </p>

                        {}
                        <div className="hidden md:block relative">
                          <div className="relative border border-foreground/15 aspect-[4/5] overflow-hidden rounded-lg shadow-md">
                            {}
                            <span aria-hidden="true" className="absolute top-2 left-2 w-3 h-3 pointer-events-none">
                              <span className="absolute top-1/2 left-0 w-full h-px bg-secondary" />
                              <span className="absolute left-1/2 top-0 h-full w-px bg-secondary" />
                            </span>
                            <span aria-hidden="true" className="absolute top-2 right-2 w-3 h-3 pointer-events-none">
                              <span className="absolute top-1/2 left-0 w-full h-px bg-secondary" />
                              <span className="absolute left-1/2 top-0 h-full w-px bg-secondary" />
                            </span>
                            <span aria-hidden="true" className="absolute bottom-2 left-2 w-3 h-3 pointer-events-none">
                              <span className="absolute top-1/2 left-0 w-full h-px bg-secondary" />
                              <span className="absolute left-1/2 top-0 h-full w-px bg-secondary" />
                            </span>
                            <span aria-hidden="true" className="absolute bottom-2 right-2 w-3 h-3 pointer-events-none">
                              <span className="absolute top-1/2 left-0 w-full h-px bg-secondary" />
                              <span className="absolute left-1/2 top-0 h-full w-px bg-secondary" />
                            </span>

                            <img src={item.image} alt={item.imageAlt} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 ease-out hover:scale-[1.03]" />
                          </div>
                          <div className="mt-3 font-mono text-[0.7rem] uppercase tracking-[0.18em] text-foreground/55">
                            FIG. {String(i + 1).padStart(2, "0")} — DETAIL
                          </div>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>;
            })}

              {}
              <div className="relative border-t border-foreground/20 pt-6 flex items-center gap-2">
                <div className="h-1 w-1 bg-secondary" />
                <div className="h-px w-16 bg-secondary" />
                <span className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-foreground/55 ml-2">
                  End of insights
                </span>
              </div>
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}